import dayjs from "dayjs";
import { deleteCookie, getCookie, setCookie } from "~/common/utils/cookie";
import { userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";
import { type AffiliatePartnerData } from "~/domains/affiliate/types/models";

import { isNonNull } from "~/common/utils/reducers";
import { selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import { useSelector } from "react-redux";
import {
  getBannerAffiliatePartner_toBeRemoved,
  getSavedAffiliatePartner_toBeRemoved,
} from "~/domains/affiliate/utils/utilsToRefactorOut";

export enum COOKIE_NAMES {
  POPUP = "affiliatePopupBannerConfirmed",
  PARTNER = "affiliatePartnerConfirmed",
  BANNER = "affiliatePartnerBannerClosed",
  MAIN = "affiliateid",
  SUBSIDIARY = "salesPartnerId",
}

export const displayAffiliatePartnerPopupBanner = (): boolean => {
  const state = userIsPublic() || userIsRegistered();
  if (!state) {
    // cleanup
    deletePreferredPartnerCookie();
    deleteAffiliatePartnerBannerClosedCookie();
    setAffiliatePopupBannerFalse();
  }
  return state;
};

export const transformSalesPartnerDTO = (dto: SalesPartnerDTO): AffiliatePartnerData => ({
  affiliateId: dto.affiliateId ?? "",
  bannerName: dto.name ?? "",
  logo: dto.logo ?? "",
  subsidiaries:
    dto.subsidiaries?.map((item) => {
      const label = [item.name, item?.address.town, item?.address.country].filter(isNonNull).join(" - ");
      return { label, value: item.code, address: item.address };
    }) ?? [],
});

export const saveSelectedSubsidiaryCookie = (subsidiaryId: string, expirationTimeHours?: number) => {
  setCookie({
    name: COOKIE_NAMES.SUBSIDIARY,
    value: subsidiaryId,
    expireDate: generateExpirationDateNow(expirationTimeHours),
  });
};
export const deleteSelectedSubsidiaryCookie = () => {
  deleteCookie(COOKIE_NAMES.SUBSIDIARY);
};

const DEFAULT_EXPIRATION_DATE = 24;
const generateExpirationDateNow = (expirationTimeHours?: number) => {
  const nrHours = expirationTimeHours && expirationTimeHours > 0 ? expirationTimeHours : DEFAULT_EXPIRATION_DATE;
  const expirationDate = dayjs().add(nrHours, "h");
  return expirationDate.toDate();
};

const checkShow = (type: COOKIE_NAMES) => {
  const partner = getBannerAffiliatePartner_toBeRemoved();
  // no partner - hide
  if (!partner || !partner.affiliateId) {
    return false;
  }

  const cookieData = getCookie(type);
  // no cookie - show
  if (!cookieData) {
    return true;
  }

  // has same partner IDs
  if (partner.affiliateId === cookieData.value) {
    // hide
    return false;
  }

  // show
  return true;
};

// POPUP
export const showAffiliatePopupBanner = () => checkShow(COOKIE_NAMES.POPUP);

export const saveAffiliatePopupClosedCookie = (expirationTimeHours?: number): void => {
  const partner = getBannerAffiliatePartner_toBeRemoved();
  if (!partner) {
    return;
  }
  setCookie({
    name: COOKIE_NAMES.POPUP,
    value: partner.affiliateId,
    expireDate: generateExpirationDateNow(expirationTimeHours),
  });
};
export const setAffiliatePopupBannerFalse = (): void => deleteCookie(COOKIE_NAMES.POPUP);

// BANNER
export const useShowAffiliateMainBanner = () => {
  const hasVisitedAffiliate = !!useSelector(selectVisitedAffiliate);
  return checkShow(COOKIE_NAMES.BANNER) && hasVisitedAffiliate;
};
export const showAffiliatePartnerSaveBtn = () => {
  if (userIsPublic()) {
    return checkShow(COOKIE_NAMES.PARTNER);
  } else {
    const savedPartner = getSavedAffiliatePartner_toBeRemoved();
    const visitedPartner = getBannerAffiliatePartner_toBeRemoved();
    return visitedPartner && savedPartner?.affiliateId !== visitedPartner.affiliateId;
  }
};
export const hasSavedPartnerCookie = () => !showAffiliatePartnerSaveBtn();

export const savePreferredPartnerCookie = (affiliateId: string, expirationTimeHours?: number) => {
  setCookie({
    name: COOKIE_NAMES.PARTNER,
    value: affiliateId,
    expireDate: generateExpirationDateNow(expirationTimeHours),
  });
};
export const deletePreferredPartnerCookie = (): void => deleteCookie(COOKIE_NAMES.PARTNER);

export const saveAffiliatePartnerBannerClosedCookie = (affiliatedId: string, expirationTimeHours?: number) => {
  setCookie({
    name: COOKIE_NAMES.BANNER,
    value: affiliatedId,
    expireDate: generateExpirationDateNow(expirationTimeHours),
  });
};
export const deleteAffiliatePartnerBannerClosedCookie = (): void => deleteCookie(COOKIE_NAMES.BANNER);
